import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ButtonBase as MuiButtonBase, skeletonClasses } from '@mui/material';
import { DataGridPro, getGridDateOperators, getGridSingleSelectOperators, GRID_CHECKBOX_SELECTION_COL_DEF, gridClasses, gridColumnVisibilityModelSelector, gridDensityFactorSelector, gridDensitySelector, gridFilterModelSelector, gridRowSelectionIdsSelector, useGridApiContext, useGridApiRef, useGridSelector } from '@mui/x-data-grid-pro';
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { useIsInDialog } from '../dialog';
import { IconArrowDownward, IconArrowUpward } from '../icon';
import { Menu } from '../menu';
import { Paper } from '../paper';
import { styled } from '../styled';
import { useRubiksTheme } from '../theme';
import { sentence } from '../typography';
import { deepMerge } from '../utils';
import { dataGridClasses } from '.';
import { DataGridCell } from './components/data-grid-cell';
import { DataGridFilterSelectOperator } from './components/data-grid-filter-select-operator';
import { ErrorOverlay } from './components/error-overlay';
import { NoRowsOverlay } from './components/no-rows-overlay';
import { SkeletonOverlay } from './components/skeleton-overlay';
import { DataGridToolbar, useToolbarSortingProps } from './components/toolbar';
import { useFirstLoad } from './components/use-first-load';
import { useInfiniteLoadingGridProps } from './components/use-infinite-loading';
import { useResize } from './components/use-resize';
export { gridClasses as dataGridClasses, gridFilterModelSelector as dataGridFilterModelSelector, gridDensitySelector as dataGridDensitySelector, gridColumnVisibilityModelSelector as dataGridColumnVisibilityModelSelector, DataGridCell, DataGridFilterSelectOperator, getGridSingleSelectOperators, gridRowSelectionIdsSelector, useGridApiRef };
// @see https://mui.com/x/react-data-grid/row-height/#static-row-height
const DATA_GRID_DEFAULT_ROW_HEIGHT = 52;
const DATA_GRID_DIALOG_HEIGHT = '60vh';
const CONTEXT_MENU_FIELD_ID = 'context-menu-field';
const singleSelectIsOperator = getGridSingleSelectOperators().find(({ value }) => value === 'is');
const dateIsOperator = getGridDateOperators().find(({ value }) => value === 'is');
export const DataGrid = React.forwardRef((props, ref) => {
    const { gridConfig, onRowClick, sortVariants = ['TOOLBAR', 'COLUMNS'], toolbarVariant = 'default', title, sortingColumn, columnVisibilityModel, columns: _columns, sortToolbarConfig: _sortToolbarConfig, rowMenu = [], onOpenRowMenu, rows: _rows, rowUpdates, pageSize, loading: _loading = false, localeText, resetFilterComponent, onFetchRows, infiniteLoading, hideFooter = true, disableRowSelectionOnClick = true, emptyState, errorState, error, initialState: _initialState, hideToolbarFilter = false, hideQuickFilter = false, hideToolbarColumns = false, hideColumnHeaders = false, borderless = false, compactCells = false, zebra = false, hideSelectAllCheckbox = false, hideToolbarAddFilterButton: disableAddFilterButton, disableRowClick, enableDensity, onDensityChange, onResetGridConfig, onFilterModelChange: _onFilterModelChange, onSortModelChange: _onSortModelChange, ...defaultProps } = props;
    const [internalSortModel, setInternalSortModel] = React.useState(props.sortModel ?? props.initialState?.sorting?.sortModel ?? []);
    const [internalFilterModel, setInternalFilterModel] = React.useState(props.filterModel ?? { items: [] });
    const sortModel = props.sortModel ?? internalSortModel;
    const filterModel = props.filterModel ?? internalFilterModel;
    const apiRef = useGridApiRef();
    const inDialog = useIsInDialog();
    React.useImperativeHandle(ref, () => apiRef.current);
    const density = props.density ?? props.initialState?.density ?? 'standard';
    const { densityFactor, muiDensityFactor } = useDataGridDensityFactor(apiRef, density);
    const localizationMemo = React.useMemo(() => getGridLocalization(localeText), [localeText]);
    const { ThemeProvider, theme } = useRubiksTheme({
        localization: localizationMemo
    });
    const hasRowMenu = rowMenu.length > 0 || typeof rowMenu === 'function';
    const { toolbarColumns, columnVisibilityModel: initialColumnVisibilityModel } = useToolbarSortingProps(_columns, _sortToolbarConfig);
    const rowMenuPropsRef = React.useRef({ rowMenu, onOpenRowMenu });
    React.useEffect(() => {
        rowMenuPropsRef.current = { rowMenu, onOpenRowMenu };
    }, [rowMenu, onOpenRowMenu]);
    const columns = React.useMemo(() => {
        const contextMenu = {
            field: CONTEXT_MENU_FIELD_ID,
            headerName: '',
            headerClassName: `${dataGridClasses.columnHeader}-contextMenu`,
            width: 50,
            resizable: false,
            filterable: false,
            disableReorder: true,
            disableColumnMenu: true,
            hideable: false,
            sortable: false,
            renderCell: ({ row }) => {
                const { onOpenRowMenu, rowMenu } = rowMenuPropsRef.current;
                return (_jsx(Menu, { onOpen: onOpenRowMenu, item: row, actions: typeof rowMenu === 'function'
                        ? rowMenu(row)
                        : rowMenu }));
            }
        };
        return [
            ...(hideSelectAllCheckbox
                ? [
                    {
                        ...GRID_CHECKBOX_SELECTION_COL_DEF,
                        renderHeader: () => null
                    }
                ]
                : []),
            ..._columns,
            ...toolbarColumns,
            contextMenu
        ]
            .filter((column) => column.field !== '')
            .map((column) => {
            const { sortable, sortFields, renderCell, headerName } = column;
            const filterOperators = (() => {
                if (infiniteLoading) {
                    if (column.filterOperators) {
                        return column.filterOperators;
                    }
                    switch (column.type) {
                        case 'singleSelect':
                            return [singleSelectIsOperator];
                        case 'date':
                            return [dateIsOperator];
                    }
                }
                return undefined;
            })();
            const renderHeader = sortFields && !sortVariants.includes('COLUMNS')
                ? (params) => (_jsx(DataGridColumnHeader, { params: params, fields: sortFields }))
                : undefined;
            return {
                filterOperators,
                renderHeader,
                ...column,
                headerName: sentence(headerName),
                headerClassName: [
                    'data-grid-header',
                    column.headerClassName
                ]
                    .filter(Boolean)
                    .join(' '),
                cellClassName: ['data-grid-cell', column.cellClassName]
                    .filter(Boolean)
                    .join(' '),
                flex: !column?.width && !column.flex ? 1 : column.flex,
                disableColumnMenu: true,
                editable: false,
                pinnable: false,
                sortable: sortVariants.includes('COLUMNS')
                    ? (sortable ?? true)
                    : false,
                renderCell: renderCell
                    ? (params) => sentence(renderCell(params))
                    : undefined
            };
        });
        // NOTE: Don't change the deps here.
        // @see https://mui.com/x/react-data-grid/column-definition/
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sortToolbarConfig = React.useMemo(() => _sortToolbarConfig ??
        columns
            .filter((col) => col.sortable)
            .map((col) => ({
            field: col.field,
            title: col.headerName ?? col.field,
            sort: 'asc'
        })), [_sortToolbarConfig, columns]);
    useResize(apiRef, gridConfig);
    const rowHeight = props.rowHeight ?? DATA_GRID_DEFAULT_ROW_HEIGHT;
    const slots = {
        toolbar: DataGridToolbar,
        loadingOverlay: SkeletonOverlay,
        noRowsOverlay: error ? ErrorOverlay : NoRowsOverlay
    };
    const _slotProps = {
        toolbar: {
            initialState: _initialState,
            loading: _loading,
            localeText,
            resetFilterComponent,
            sortModel,
            filterModel,
            columns,
            sortToolbarConfig: sortVariants.includes('TOOLBAR')
                ? sortToolbarConfig
                : undefined,
            variant: toolbarVariant,
            title,
            sortingColumn,
            hideToolbarFilter,
            hideQuickFilter,
            hideToolbarColumns,
            showBottomBorder: toolbarVariant === 'reduced' && hideColumnHeaders,
            density,
            enableDensity,
            onDensityChange,
            gridConfig,
            onResetGridConfig: () => {
                void gridConfig?.reset();
                onResetGridConfig?.();
            },
            onSortModelChange: (sortModel) => {
                setInternalSortModel(sortModel);
                return (infiniteLoadingProps.onSortModelChange ??
                    _onSortModelChange)?.(sortModel, {
                    api: apiRef.current,
                    reason: undefined
                });
            },
            onFilterModelChange: (filterModel) => {
                setInternalFilterModel(filterModel);
                return (infiniteLoadingProps.onFilterModelChange ??
                    _onFilterModelChange)?.(filterModel, {
                    api: apiRef.current,
                    reason: undefined
                });
            }
        },
        loadingOverlay: {
            rowHeight,
            gridConfig
        },
        columnsManagement: {
            getTogglableColumns: (columns) => {
                return columns
                    .filter((column) => column.hideable !== false)
                    .map((column) => column.field);
            },
            disableShowHideToggle: true,
            disableResetButton: true
        },
        filterPanel: {
            disableAddFilterButton
        }
    };
    const infiniteLoadingProps = useInfiniteLoadingGridProps({
        ...props,
        slots,
        slotProps: _slotProps,
        apiRef,
        onFetchRows,
        rowHeight
    });
    const rows = useUpdatedRows(infiniteLoadingProps.rows ?? _rows, rowUpdates);
    const loading = infiniteLoadingProps.loading ?? _loading;
    const firstLoadOrReload = useFirstLoad(loading, {
        sortModel,
        filterModel
    });
    const noRowsOverlay = (() => {
        if (error) {
            return {
                ...errorState,
                errorMessage: error.message
            };
        }
        if (firstLoadOrReload) {
            return undefined;
        }
        return emptyState;
    })();
    const slotProps = deepMerge({}, _slotProps, infiniteLoadingProps.slotProps ?? {}, { noRowsOverlay });
    if (process.env.NODE_ENV === 'development') {
        validateRowIds(rows);
    }
    const initialState = deepMerge({
        sorting: {
            sortModel
        },
        pagination: {
            paginationModel: { pageSize }
        },
        pinnedColumns: {
            right: [
                hasRowMenu ? CONTEXT_MENU_FIELD_ID : undefined
            ].filter((field) => Boolean(field))
        },
        columns: {
            columnVisibilityModel: {
                id: false,
                [CONTEXT_MENU_FIELD_ID]: hasRowMenu,
                ...initialColumnVisibilityModel
            }
        }
    }, _initialState ?? {});
    const getRowClassName = (params) => {
        return [
            zebra && params.indexRelativeToCurrentPage % 2 === 1
                ? 'zebra-row'
                : undefined,
            disableRowClick?.(params.row) ? 'default-cursor-row' : undefined
        ]
            .filter(Boolean)
            .join(' ');
    };
    const stringifiedColumnVisibilityModel = [
        JSON.stringify(initialState.columns?.columnVisibilityModel),
        JSON.stringify(columnVisibilityModel)
    ];
    const mergedColumnVisibilityModel = React.useMemo(() => {
        return deepMerge({}, initialState.columns?.columnVisibilityModel ?? {}, columnVisibilityModel ?? {});
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    stringifiedColumnVisibilityModel);
    return (_jsx(ThemeProvider, { theme: theme, children: _jsx(DataGridPaper, { elevation: 0, children: _jsx(StyledDataGridPro, { ...defaultProps, sortingOrder: props.sortingOrder ?? ['asc', 'desc'], inDialog: inDialog, hasRowClick: Boolean(onRowClick), hideColumnHeaders: hideColumnHeaders, borderless: borderless, compactCells: compactCells, gridConfig: gridConfig, rowHeight: rowHeight, columnHeaderHeight: hideColumnHeaders
                    ? 0
                    : muiDensityFactor
                        ? DATA_GRID_DEFAULT_ROW_HEIGHT /
                            muiDensityFactor
                        : 0, showToolbar: true, apiRef: apiRef, density: density, slots: slots, sortModel: sortModel, filterModel: filterModel, columns: columns, columnVisibilityModel: mergedColumnVisibilityModel, getRowId: (row) => row.databaseId ?? row.id ?? -1, getRowHeight: () => rowHeight * densityFactor, initialState: initialState, onRowClick: (params) => {
                    if (disableRowClick?.(params.row) ||
                        !onRowClick) {
                        return;
                    }
                    onRowClick(params.row);
                }, onSortModelChange: (model, details) => {
                    setInternalSortModel(model);
                    _onSortModelChange?.(model, details);
                    apiRef.current?.scrollToIndexes({ rowIndex: 0 });
                }, onFilterModelChange: (model, details) => {
                    setInternalFilterModel(model);
                    _onFilterModelChange?.(model, details);
                }, getRowClassName: getRowClassName, disableRowSelectionOnClick: disableRowSelectionOnClick, hideFooter: hideFooter, ...infiniteLoadingProps, slotProps: slotProps, loading: loading, rows: rows }) }) }));
});
const DataGridPaper = styled(Paper)({
    display: 'flex',
    flex: 1,
    minHeight: 0,
    width: '100%'
});
/**
 * Removed the focus outline from the cells and column headers.
 * Please remove once accessibility becomes a requirement.
 * @see https://mui.com/x/react-data-grid/style-recipes/#remove-cell-focus-outline
 */
const outlineClasses = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none'
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none'
    }
};
const StyledDataGridProBase = styled(DataGridPro)(({ theme, inDialog, hasRowClick, gridConfig, hideColumnHeaders, borderless, compactCells }) => ({
    ...outlineClasses,
    [`&.${gridClasses.root}`]: {
        minHeight: 350,
        height: inDialog ? DATA_GRID_DIALOG_HEIGHT : undefined,
        '--DataGrid-t-color-background-base': theme.palette.background.paper,
        ...(borderless
            ? {
                border: 0,
                borderRadius: 0
            }
            : {}),
        ...(hideColumnHeaders
            ? {
                '--DataGrid-headerHeight': '0px',
                '--DataGrid-topContainerHeight': '0px'
            }
            : {})
    },
    [`& .data-grid-header, & .${gridClasses.columnHeader}`]: {
        backgroundColor: theme.palette.background.paper,
        borderTop: hideColumnHeaders ? 0 : '1px solid',
        borderBottom: hideColumnHeaders ? 0 : undefined,
        borderColor: theme.palette.components?.input?.outlined?.['enabled-border'],
        [`.${gridClasses.columnHeaderTitle}`]: {
            fontSize: theme.components?.table?.header?.fontSize
        },
        [`.${gridClasses.columnHeaderTitleContainerContent}, .${gridClasses.columnSeparator}`]: {
            visibility: gridConfig?.loading ? 'hidden' : undefined
        }
    },
    ...(hideColumnHeaders
        ? {
            [`& .${gridClasses.columnHeaders}`]: {
                display: 'none'
            }
        }
        : {}),
    [`& .${gridClasses.scrollbar}`]: {
        backgroundColor: theme.palette.background.paper,
        [`&.${gridClasses['scrollbar--vertical']}`]: {
            borderLeft: '1px solid var(--DataGrid-rowBorderColor)'
        }
    },
    [`& .${gridClasses.scrollbarFiller}`]: {
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid',
        borderColor: theme.palette.components?.input?.outlined?.['enabled-border']
    },
    [`& .${gridClasses.columnHeaders} .${gridClasses.filler}`]: {
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid',
        borderColor: theme.palette.components?.input?.outlined?.['enabled-border']
    },
    [`& .${gridClasses.filler}`]: {
        backgroundColor: theme.palette.background.paper
    },
    [`& .${gridClasses['filler--pinnedRight']}`]: {
        backgroundColor: theme.palette.background.paper,
        borderLeftColor: 'transparent'
    },
    [`& .${gridClasses.scrollbarFiller}, & .${gridClasses.scrollbarFiller}.${gridClasses['scrollbarFiller--pinnedRight']}`]: {
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid var(--DataGrid-rowBorderColor)'
    },
    '& .default-cursor-row': {
        cursor: 'default',
        '& .MuiDataGrid-cell': {
            cursor: 'default'
        }
    },
    '& .zebra-row': {
        backgroundColor: theme.palette.action.hover,
        [`.${gridClasses.cell}--pinnedRight`]: {
            background: `linear-gradient(${theme.palette.action.hover}), ${theme.palette.background.paper}`
        }
    },
    [`& .${gridClasses.virtualScrollerContent} .${gridClasses.row}`]: {
        [`.${gridClasses.cell}`]: {
            fontSize: theme.typography?.body2?.fontSize,
            ...(compactCells
                ? {
                    border: 0,
                    padding: 0
                }
                : {})
        },
        [`.${gridClasses.cell}--pinnedRight`]: {
            backgroundColor: theme.palette.background.paper
        },
        [`& .${gridClasses.cell}--withLeftBorder`]: {
            borderLeftColor: 'transparent'
        },
        '&:hover': {
            cursor: hasRowClick ? 'pointer' : 'default',
            [`.${gridClasses['cell--pinnedRight']}, .${gridClasses['scrollbarFiller--pinnedRight']}`]: {
                background: `linear-gradient(${theme.palette.primary._states?.hover}), ${theme.palette.background.paper}`
            },
            '[data-role="chip-group"] [data-role="chip-group-fade-out"]': {
                background: `linear-gradient(${theme.palette.primary._states?.hover}), ${theme.palette.background.paper}`
            }
        }
    },
    [`& .${gridClasses.columnHeader}-facilityInfo .${gridClasses.columnSeparator}`]: {
        display: 'none'
    },
    [`& .${gridClasses.columnHeader}-asyncFacilityInfo .${gridClasses['columnSeparator--sideRight']}`]: {
        display: 'none'
    },
    [`& .${skeletonClasses.root}`]: {
        backgroundColor: theme.palette.action?.hover
    },
    [`& .${gridClasses.columnHeader}:focus .${gridClasses.columnSeparator}, & .${gridClasses['columnHeader--siblingFocused']} .${gridClasses.columnSeparator}`]: {
        opacity: 1
    },
    [`& .${gridClasses.scrollShadow}`]: {
        boxShadow: 'none'
    }
}));
const StyledDataGridPro = StyledDataGridProBase;
const getGridLocalization = (localeText) => {
    if (!localeText) {
        return undefined;
    }
    return {
        components: {
            MuiDataGrid: {
                defaultProps: {
                    localeText
                }
            }
        }
    };
};
function useUpdatedRows(rows, rowUpdates) {
    return React.useMemo(() => {
        if (!rowUpdates || rowUpdates.length === 0) {
            return rows;
        }
        function getId(row) {
            return row.databaseId ?? row.id ?? -1;
        }
        return rowUpdates.reduce((acc, repl) => {
            const rowId = getId(repl.row);
            const index = acc.findIndex((row) => getId(row) === rowId);
            if (repl.action === 'CREATED') {
                if (index === -1) {
                    acc.unshift(repl.row);
                }
            }
            if (repl.action === 'UPDATED') {
                if (index !== -1) {
                    acc[index] = repl.row;
                }
            }
            if (repl.action === 'DELETED') {
                if (index !== -1) {
                    acc.splice(index, 1);
                }
            }
            return acc;
        }, [...rows]);
    }, [rowUpdates, rows]);
}
export function useDataGridDensity() {
    const apiRef = useGridApiContext();
    return useGridSelector(apiRef, gridDensitySelector);
}
const DENSITY_FACTOR_COMFORTABLE = 1.72;
export function useDataGridDensityFactor(apiRef, initialDensity) {
    const [densityFactor, setDensityFactor] = React.useState(1);
    const [muiDensityFactor, setMuiDensityFactor] = React.useState(0);
    React.useEffect(() => {
        const onDensityChange = (newDensity) => {
            if (apiRef.current) {
                const currentDensityFactor = gridDensityFactorSelector(apiRef);
                setDensityFactor(newDensity === 'comfortable'
                    ? DENSITY_FACTOR_COMFORTABLE
                    : currentDensityFactor);
                setMuiDensityFactor(currentDensityFactor);
            }
        };
        if (initialDensity) {
            onDensityChange(initialDensity);
        }
        return apiRef.current?.subscribeEvent('densityChange', onDensityChange);
    }, [apiRef, initialDensity]);
    return { densityFactor, muiDensityFactor };
}
function validateRowIds(rows) {
    if (!rows?.length) {
        return;
    }
    if (rows.some((row) => row.databaseId === undefined && row.id === undefined)) {
        console.warn('[DataGrid] DataGrid rows must have either a "databaseId" or "id" property defined.');
    }
    const ids = new Set();
    rows.forEach((row) => {
        const id = row.databaseId ?? row.id;
        if (id !== undefined && ids.has(id)) {
            console.warn(`[DataGrid] Found duplicate row id: ${id}`);
        }
        if (id !== undefined) {
            ids.add(id);
        }
    });
}
function DataGridColumnHeader(props) {
    const apiRef = useGridApiContext();
    const ref = React.useRef(null);
    const sortModel = apiRef.current.getSortModel();
    const sortField = sortModel[0]?.field;
    const isSortedDesc = sortModel[0]?.sort === 'desc';
    const hasSorting = props.fields.includes(sortField);
    const Icon = isSortedDesc ? IconArrowDownward : IconArrowUpward;
    React.useEffect(() => {
        if (hasSorting) {
            const classList = ref.current?.closest(`.${gridClasses.columnHeader}`)?.classList;
            classList?.add(gridClasses['columnHeader--sorted']);
            return () => {
                classList?.remove(gridClasses['columnHeader--sorted']);
            };
        }
        return;
    }, [hasSorting]);
    return (_jsxs(FlexBox, { ref: ref, vAlign: "center", children: [_jsx(Box, { component: "span", className: gridClasses.columnHeaderTitle, sx: {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontWeight: 'var(--unstable_DataGrid-headWeight)',
                    lineHeight: 'normal'
                }, children: props.params.colDef.headerName }), hasSorting && (_jsx(Box, { className: gridClasses.iconButtonContainer, children: _jsx(MuiButtonBase, { className: gridClasses.sortButton, disabled: true, sx: {
                        width: 28,
                        height: 28,
                        padding: '5px',
                        fontSize: '1.2857142857142858rem'
                    }, children: _jsx(Icon, { className: gridClasses.sortIcon, color: "text.disabled" }) }) }))] }));
}
